import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, ReactNode } from "react";
import {
  Activity, ArrowDownToLine, ArrowLeft, ArrowRight, ArrowUp, Ban, Blend, Box, Brush, ChevronLeft, Clapperboard, Heart, Maximize2,
  AudioWaveform, Bomb, Highlighter, Play, ChevronDown, ChevronsDown, EyeOff, Flame, Keyboard, Lightbulb, Palette, PenTool, Pencil, RotateCw, Sparkles, Spline, Type, Vibrate, Waves, Zap, ZoomIn, ZoomOut,
} from "lucide-react";
import { useDesign } from "../state/designStore";
import { rgbFloatsToHex } from "../lib/color";
import { SliderControl } from "./SliderControl";
import { MoveControl, RotationControl } from "./PositionRotation";
import { DrawingToolPopover } from "./DrawingToolPopover";
import { TextLayersBar } from "./TextLayersBar";
import { EffectsStudio } from "./EffectsStudio";
import { FontPickerModal } from "./FontPickerModal";
import { ColorPickerModal } from "./ColorPickerModal";
import { EffectsControl } from "./EffectsControl";
import { StickerCanvas } from "./StickerCanvas";
import { ToolDock } from "./ToolDock";
import type { DockTool } from "./ToolDock";
import { ColorOut } from "../types";

const BLANK_TEMPLATE_ID = "_blank_transparent";
const DEFAULT_FONT_SIZE = 60;
const DEFAULT_STROKE_WIDTH = 12;

const MOTION_GROUPS = [
  {
    title: "دخول",
    items: [
      { id: "fade", label: "ظهور", Icon: Sparkles },
      { id: "pop", label: "ارتداد", Icon: Zap },
      { id: "zoom_in", label: "تكبير", Icon: ZoomIn },
      { id: "slide_up", label: "صعود", Icon: ArrowUp },
      { id: "slide_left", label: "من اليسار", Icon: ArrowRight },
      { id: "slide_right", label: "من اليمين", Icon: ArrowLeft },
      { id: "spin", label: "دوران", Icon: RotateCw },
      { id: "drop", label: "سقوط", Icon: ArrowDownToLine },
      { id: "bounce", label: "نطّ", Icon: ChevronsDown },
    ],
  },
  {
    title: "مستمرة",
    items: [
      { id: "float", label: "طفو", Icon: Waves },
      { id: "pulse", label: "نبض", Icon: Heart },
      { id: "swing", label: "أرجحة", Icon: Activity },
      { id: "shake", label: "اهتزاز", Icon: Vibrate },
      { id: "neon_glow", label: "نيون", Icon: Lightbulb },
      { id: "flicker", label: "وميض", Icon: Flame },
    ],
  },
  {
    title: "حرف بحرف",
    items: [
      { id: "typewriter", label: "كتابة", Icon: Keyboard },
      { id: "wave", label: "موجة", Icon: AudioWaveform },
      { id: "explode", label: "انفجار", Icon: Bomb },
    ],
  },
  {
    title: "خروج",
    items: [
      { id: "fade_out", label: "اختفاء", Icon: EyeOff },
      { id: "zoom_out", label: "تصغير", Icon: ZoomOut },
    ],
  },
] as const;

// Numeric codes match Lottie's own stroke "lc"/"lj" values, which
// config.py's generate_sticker() passes straight through.
const LINE_CAP_OPTIONS = [
  { value: 1, label: "حاد" }, // Butt
  { value: 2, label: "دائري" }, // Round
  { value: 3, label: "مربّع" }, // Square
];
const LINE_JOIN_OPTIONS = [
  { value: 1, label: "حاد" }, // Miter
  { value: 2, label: "دائري" }, // Round
  { value: 3, label: "مشطوف" }, // Bevel
];

type ToolKey = "text" | "size" | "color" | "gradient" | "curve" | "stroke" | "depth" | "pen" | "motion";

const TOOL_TITLES: Record<ToolKey, string> = {
  text: "النص",
  size: "الحجم والمد",
  color: "اللون واللمعة",
  gradient: "التدرّج اللوني",
  curve: "النص المنحني",
  stroke: "الحدود",
  depth: "3D - العمق ثلاثي الأبعاد",
  pen: "رسم الاسم بالقلم",
  motion: "الحركة والتأثيرات",
};

/** بطاقة قسم داخل لوحة الأداة */
function Section({ title, hint, children }: { title?: string; hint?: string; children: ReactNode }) {
  return (
    <div
      className="rounded-2xl p-3.5 flex flex-col gap-4"
      style={{ background: "rgba(255,255,255,.045)", border: "1px solid rgba(255,255,255,.08)" }}
    >
      {title && (
        <div>
          <h3 className="text-[13px] font-semibold">{title}</h3>
          {hint && <p className="text-[11px] text-tg-hint mt-0.5">{hint}</p>}
        </div>
      )}
      {children}
    </div>
  );
}

function SwitchRow({
  title, desc, icon, checked, onChange,
}: {
  title: string; desc: string; icon?: ReactNode; checked: boolean; onChange: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-start gap-2.5 min-w-0">
        {icon && (
          <span className="mt-0.5 w-8 h-8 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <span className="block text-sm font-medium">{title}</span>
          <span className="block text-xs text-tg-hint">{desc}</span>
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={`w-12 h-7 rounded-full relative transition-colors shrink-0 ${checked ? "bg-tg-button" : "bg-white/15"}`}
      >
        <span
          className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-transform ${
            checked ? "translate-x-[-1.375rem]" : "translate-x-[-0.25rem]"
          } right-0`}
        />
      </button>
    </div>
  );
}

/** A swatch button that opens the full color picker modal. */
function ColorSwatchTrigger({ label, hex, onOpen }: { label: string; hex: string; onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full flex items-center gap-3 glass-pill rounded-2xl px-4 py-3.5 text-right"
    >
      <span className="w-9 h-9 rounded-full border-2 border-white/20 shrink-0" style={{ backgroundColor: hex }} />
      <span className="flex-1">
        <span className="block text-sm text-tg-hint">{label}</span>
        <span className="block text-xs uppercase text-tg-hint" dir="ltr">
          {hex}
        </span>
      </span>
      <ChevronLeft size={18} className="text-tg-hint shrink-0" />
    </button>
  );
}

/** تنبيه جميل يطلع لما تضغط أداة: يبيّن اسمها ويدل المستخدم ينزل لها */
function ToolToast({ label, Icon, below, onGo }: { label: string; Icon: DockTool["Icon"]; below: boolean; onGo: () => void }) {
  return (
    <button
      type="button"
      onClick={onGo}
      className="tool-toast fixed z-40 flex items-center gap-2.5 rounded-full ps-2 pe-4 py-2 text-sm font-semibold text-white"
      style={{
        top: 70,
        left: "50%",
        background: "linear-gradient(135deg, rgba(76,141,255,.97), rgba(47,91,234,.97))",
        boxShadow: "0 12px 30px -8px rgba(59,130,246,.7), inset 0 1px 0 rgba(255,255,255,.35)",
        border: "1px solid rgba(255,255,255,.25)",
      }}
    >
      <span className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
        <Icon size={17} strokeWidth={2.3} />
      </span>
      <span>
        {label}
        {below && <span className="font-normal opacity-90"> · أدواتها بالأسفل</span>}
      </span>
      {below && <ChevronDown size={18} strokeWidth={2.6} className="tool-toast-arrow" />}
    </button>
  );
}

export function ControlsPanel() {
  const { design, template, activeText, activeEffect, activeLayerKind, updateActiveText, updateActivePosition, historyEpoch } =
    useDesign();
  const textInputRef = useRef<HTMLInputElement>(null);
  const [tool, setTool] = useState<ToolKey | null>("text");
  const [fontModalOpen, setFontModalOpen] = useState(false);
  const [drawingPopoverOpen, setDrawingPopoverOpen] = useState(false);
  const [textColorModalOpen, setTextColorModalOpen] = useState(false);
  const [strokeColorModalOpen, setStrokeColorModalOpen] = useState(false);
  const [outlineColorModalOpen, setOutlineColorModalOpen] = useState(false);
  const [shineColorModalOpen, setShineColorModalOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const canvasWrapRef = useRef<HTMLDivElement>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [toast, setToast] = useState<{ id: number; label: string; Icon: DockTool["Icon"]; below: boolean } | null>(null);
  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  // IMPORTANT: this input is intentionally UNCONTROLLED (defaultValue,
  // no `value` prop). A controlled <input value={...}> rewrites the DOM
  // node's value on every keystroke's re-render; combined with Arabic
  // (RTL) text, some WebViews then reset the caret to position 0 instead
  // of preserving it - so every next character gets inserted at the
  // FRONT of the string instead of where the user is typing, silently
  // building the text backwards ("حسن" -> "نسح").
  // The input is only reset when switching to a DIFFERENT text layer or
  // template, via the `key` below.
  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    updateActiveText({ text: e.target.value });
  };

  if (!design || !template) return null;

  const isEffect = activeLayerKind === "effect" && !!activeEffect;
  const isIcon = !!activeText?.is_icon;
  const isTextOnly = template.id === BLANK_TEMPLATE_ID;

  const hasStroke = !!template.default_stroke;
  const hasOutline = !!template.default_outline;
  const isOutlineOn = activeText ? activeText.outline_enabled ?? hasOutline : false;
  const isBackOrder = activeText?.z_order === "back";
  const shineStyle = activeText?.shine_style ?? null;
  const isPenDraw = !!activeText?.reveal_style;

  const textColorHex = activeText ? activeText.text_color ?? (template.colors[0] ? rgbFloatsToHex(template.colors[0].rgb) : "#000000") : "#000000";
  const strokeColorHex = activeText
    ? activeText.stroke_color ?? (template.colors[0] ? rgbFloatsToHex(template.colors[0].rgb) : "#000000")
    : "#000000";
  const outlineColorHex = activeText ? activeText.outline_color ?? "#000000" : "#000000";
  const shineColorHex = activeText?.shine_color ?? null;

  // ---- الأدوات المتاحة حسب نوع الطبقة المحددة ----
  const T = {
    text: { key: "text", label: "النص", Icon: Type, badge: design.texts.length > 1 ? design.texts.length : false },
    size: { key: "size", label: "الحجم", Icon: Maximize2 },
    color: { key: "color", label: "الألوان", Icon: Palette, badge: !!shineStyle },
    gradient: { key: "gradient", label: "التدرّج", Icon: Blend, badge: !!activeText?.gradient_color },
    curve: { key: "curve", label: "منحني", Icon: Spline, badge: !!activeText && activeText.curve_amount !== 0 },
    stroke: { key: "stroke", label: "الحدود", Icon: Brush },
    depth: { key: "depth", label: "3D", Icon: Box, badge: isOutlineOn },
    pen: { key: "pen", label: "رسم الاسم", Icon: PenTool, badge: isPenDraw },
    motion: { key: "motion", label: "الحركة", Icon: Clapperboard, badge: !!activeText?.motion_style || design.effects.length > 0 },
  } satisfies Record<ToolKey, DockTool>;

  const drawTool: DockTool = { key: "draw", label: "رسم حر", Icon: Pencil, onClick: () => setDrawingPopoverOpen(true) };
  const pages: DockTool[][] = isEffect
    ? [[T.text, T.motion]]
    : isIcon
      ? [[T.text, T.color, T.size, T.motion]]
      : [
          [T.text, T.size, T.color, ...(hasStroke ? [T.stroke] : []), T.depth],
          [T.gradient, T.curve, T.pen, T.motion, ...(isTextOnly ? [drawTool] : [])],
        ];
  const tools: DockTool[] = pages.flat().filter((t) => !t.onClick);

  const currentKey: ToolKey | null = tool && tools.some((t) => t.key === tool) ? tool : tool === null ? null : (tools[0].key as ToolKey);

  const scrollToPanel = () => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setToast(null);
  };

  const handleSelect = (key: string) => {
    // المس الأداة النشطة مرة ثانية = تسكير اللوحة (الملصق يبان أكبر)
    if (currentKey === key) {
      setTool(null);
      setToast(null);
      return;
    }
    setTool(key as ToolKey);
    const meta = tools.find((t) => t.key === key);
    if (!meta) return;
    // بعد ما تنرسم اللوحة: نشوف إذا هي تحت الشاشة، ونطلع التنبيه + وميض خفيف على اللوحة
    window.setTimeout(() => {
      const el = panelRef.current;
      const below = el ? el.getBoundingClientRect().top > window.innerHeight - 170 : true;
      const id = Date.now();
      setToast({ id, label: TOOL_TITLES[key as ToolKey], Icon: meta.Icon, below });
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast((t) => (t && t.id === id ? null : t)), 2600);
      el?.animate(
        [
          { boxShadow: "0 0 0 0 rgba(59,130,246,0)" },
          { boxShadow: "0 0 0 3px rgba(59,130,246,.85), 0 0 24px 4px rgba(59,130,246,.3)" },
          { boxShadow: "0 0 0 0 rgba(59,130,246,0)" },
        ],
        { duration: 1100, easing: "ease-out" },
      );
    }, 60);
  };

  // ---------------- محتوى كل أداة ----------------
  const renderText = () => (
    <>
      <Section>
        <TextLayersBar />
      </Section>

      {activeText && !isIcon && !isEffect && (
        <Section title="النص">
          <div>
            <div
              className="relative rounded-2xl bg-tg-secondaryBg focus-within:ring-2 focus-within:ring-tg-button transition-shadow cursor-text"
              onClick={() => textInputRef.current?.focus()}
            >
              <input
                key={`${template.id}-${activeText.id}-${historyEpoch}`}
                ref={textInputRef}
                type="text"
                defaultValue={activeText.text}
                maxLength={template.max_name_length}
                onChange={handleTextChange}
                placeholder="اضغط هنا للكتابة..."
                dir="rtl"
                className="w-full bg-transparent rounded-2xl pr-4 pl-12 py-3.5 text-tg-text text-base outline-none placeholder:text-tg-hint"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setFontModalOpen(true);
                }}
                aria-label="اختر الخط"
                className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full glass-pill flex items-center justify-center text-tg-link"
              >
                <Type size={16} strokeWidth={2.25} />
              </button>
            </div>
            <div className="flex justify-between text-xs text-tg-hint mt-1.5">
              <span>الخط: اضغط الأيقونة يسار الحقل</span>
              <span>
                {activeText.text.length}/{template.max_name_length}
              </span>
            </div>
          </div>
        </Section>
      )}

      {activeText && isIcon && (
        <div className="flex items-center justify-center py-2">
          <span className="text-4xl">{activeText.text}</span>
        </div>
      )}

      <Section title="الموضع">
        <MoveControl />
        {activeText && !isEffect && (
          <>
            <div className="h-px bg-white/10" />
            <SwitchRow
              title="النص بالخلف"
              desc="يخلي النص خلف عناصر القالب بدل قدّامها"
              checked={isBackOrder}
              onChange={() => updateActiveText({ z_order: isBackOrder ? "front" : "back" })}
            />
          </>
        )}
      </Section>

      <Section title="الدوران" hint="اسحب المقبض على القرص، أو اكتب الزاوية، أو استخدم الأزرار">
        <RotationControl />
      </Section>
    </>
  );

  const renderSize = () =>
    activeText && (
      <>
        <Section title="الحجم والمد">
          {!isIcon && (
            <SliderControl
              label="حجم الخط"
              value={activeText.font_size ?? DEFAULT_FONT_SIZE}
              min={24}
              max={260}
              step={2}
              onChange={(v) => updateActiveText({ font_size: v })}
            />
          )}
          <SliderControl
            label="تكبير/تصغير (Zoom)"
            value={activeText.position.scale}
            min={0.25}
            max={4}
            step={0.05}
            unit="x"
            onChange={(v) => updateActivePosition({ scale: v })}
          />
          <SliderControl
            label="مد أفقي"
            value={activeText.stretch_x}
            min={0.25}
            max={4}
            step={0.05}
            unit="x"
            onChange={(v) => updateActiveText({ stretch_x: v })}
          />
          <SliderControl
            label="مد عمودي"
            value={activeText.stretch_y}
            min={0.25}
            max={4}
            step={0.05}
            unit="x"
            onChange={(v) => updateActiveText({ stretch_y: v })}
          />
        </Section>
      </>
    );

  const renderColor = () =>
    activeText && (
      <>
        <Section title={isIcon ? "لون الشعار" : "لون النص"}>
          <ColorSwatchTrigger
            label={isIcon ? "لون الشعار" : "لون النص"}
            hex={textColorHex}
            onOpen={() => setTextColorModalOpen(true)}
          />
        </Section>

        {!isIcon && (
          <Section title="لمعة على النص" hint="تأثير لمعان يمر على الحروف">
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => updateActiveText({ shine_style: shineStyle === "sweep" ? null : "sweep" })}
                className={`flex-1 px-3 py-2.5 rounded-xl text-sm ${
                  shineStyle === "sweep" ? "bg-tg-button text-tg-buttonText" : "bg-white/10"
                }`}
              >
                لمعة عابرة
              </button>
              <button
                type="button"
                onClick={() => updateActiveText({ shine_style: shineStyle === "shimmer" ? null : "shimmer" })}
                className={`flex-1 px-3 py-2.5 rounded-xl text-sm ${
                  shineStyle === "shimmer" ? "bg-tg-button text-tg-buttonText" : "bg-white/10"
                }`}
              >
                لمعان مستمر
              </button>
            </div>
            {shineStyle && (
              <ColorSwatchTrigger
                label="لون اللمعان"
                hex={shineColorHex ?? (shineStyle === "shimmer" ? "#FFD700" : "#FFFFFF")}
                onOpen={() => setShineColorModalOpen(true)}
              />
            )}
          </Section>
        )}
      </>
    );

  const renderStroke = () =>
    activeText && (
      <>
        {hasStroke && (
          <Section title="الحدود (Stroke)">
            <ColorSwatchTrigger label="لون الحدود" hex={strokeColorHex} onOpen={() => setStrokeColorModalOpen(true)} />
            <SliderControl
              label="سماكة الحدود"
              value={activeText.stroke_width ?? DEFAULT_STROKE_WIDTH}
              min={0}
              max={30}
              onChange={(v) => updateActiveText({ stroke_width: v })}
            />
            <SliderControl
              label="شفافية الحدود"
              value={activeText.stroke_opacity ?? 100}
              min={0}
              max={100}
              unit="%"
              onChange={(v) => updateActiveText({ stroke_opacity: v })}
            />
            <div>
              <label className="block text-sm text-tg-hint mb-2">شكل نهاية الخط</label>
              <div className="flex gap-2">
                {LINE_CAP_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateActiveText({ stroke_line_cap: opt.value })}
                    className={`flex-1 py-2 rounded-xl text-sm ${
                      (activeText.stroke_line_cap ?? 2) === opt.value ? "bg-tg-button text-tg-buttonText" : "glass-pill text-tg-text"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm text-tg-hint mb-2">شكل زوايا الحدود</label>
              <div className="flex gap-2">
                {LINE_JOIN_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => updateActiveText({ stroke_line_join: opt.value })}
                    className={`flex-1 py-2 rounded-xl text-sm ${
                      (activeText.stroke_line_join ?? 2) === opt.value ? "bg-tg-button text-tg-buttonText" : "glass-pill text-tg-text"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </Section>
        )}
      </>
    );

  const renderDepth = () =>
    activeText && (
      <>
        <Section title="العمق (ثلاثي الأبعاد)">
          <SwitchRow
            title="نص ثلاثي الأبعاد"
            desc="نسخة مزاحة خلف النص تعطيه إحساس بالعمق"
            checked={isOutlineOn}
            onChange={() => updateActiveText({ outline_enabled: !isOutlineOn })}
          />
          {isOutlineOn && (
            <>
              <ColorSwatchTrigger label="لون البعد الثالث" hex={outlineColorHex} onOpen={() => setOutlineColorModalOpen(true)} />
              <SliderControl
                label="الإزاحة الأفقية"
                value={activeText.outline_offset_x ?? 3}
                min={-40}
                max={40}
                onChange={(v) => updateActiveText({ outline_offset_x: v })}
              />
              <SliderControl
                label="الإزاحة العمودية"
                value={activeText.outline_offset_y ?? 3}
                min={-40}
                max={40}
                onChange={(v) => updateActiveText({ outline_offset_y: v })}
              />
            </>
          )}
        </Section>
      </>
    );

  const renderMotion = () => (
    <>
      {activeText && !isEffect && !isIcon && (
        <Section title="حركة جاهزة للنص" hint="اختر حركة وحدة، المس نفسها مرة ثانية لإلغائها">
          <div className="grid grid-cols-4 gap-2">
            <button
              type="button"
              onClick={() => updateActiveText({ motion_style: null })}
              className={`flex flex-col items-center gap-1 py-2.5 rounded-xl text-[11px] border ${
                !activeText.motion_style ? "bg-tg-button text-tg-buttonText border-transparent" : "bg-white/5 border-white/10"
              }`}
            >
              <Ban size={17} strokeWidth={2} />
              بدون
            </button>
          </div>
          {MOTION_GROUPS.map((g) => (
            <div key={g.title} className="mt-3">
              <div className="text-[11px] text-tg-hint mb-1.5 px-0.5">{g.title}</div>
              <div className="grid grid-cols-4 gap-2">
                {g.items.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => updateActiveText({ motion_style: activeText.motion_style === id ? null : id })}
                    className={`flex flex-col items-center gap-1 py-2.5 rounded-xl text-[11px] border ${
                      activeText.motion_style === id ? "bg-tg-button text-tg-buttonText border-transparent" : "bg-white/5 border-white/10"
                    }`}
                  >
                    <Icon size={17} strokeWidth={2} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </Section>
      )}
      <Section>
        <EffectsStudio />
      </Section>
    </>
  );

  const renderGradient = () =>
    activeText && (
      <Section>
        <EffectsControl colorPresets={template.colors} part="gradient" />
      </Section>
    );

  const renderCurve = () =>
    activeText && (
      <Section>
        <EffectsControl colorPresets={template.colors} part="curve" />
      </Section>
    );

  const penChip = <T extends string>(
    options: { id: T; label: string }[],
    value: T,
    onPick: (v: T) => void,
  ) => (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onPick(o.id)}
          className="px-3 h-8 rounded-full text-xs font-medium border transition-colors"
          style={{
            background: value === o.id ? "rgba(59,130,246,.22)" : "rgba(255,255,255,.05)",
            borderColor: value === o.id ? "rgba(59,130,246,.8)" : "rgba(255,255,255,.1)",
            color: value === o.id ? "#fff" : undefined,
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );

  // يرجّعك للملصق (يمرر الصفحة له) وبعدها يبدأ الرسم من أوله حتى تشوفه
  const replayPen = () => {
    const el = canvasWrapRef.current;
    const far = !!el && (el.getBoundingClientRect().top < 56 || el.getBoundingClientRect().bottom > window.innerHeight - 24);
    if (el && far) el.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(
      () => window.dispatchEvent(new CustomEvent("sticker:replay", { detail: { fraction: activeText?.start_fraction ?? 0, now: true } })),
      far ? 420 : 0,
    );
  };
  // أي تعديل بإعدادات الرسم: المعاينة تعيد التشغيل من بداية النص بس تجهز
  const penSet = (patch: Partial<NonNullable<typeof activeText>>) => {
    updateActiveText(patch);
    window.dispatchEvent(new CustomEvent("sticker:replay", { detail: { fraction: activeText?.start_fraction ?? 0, now: false } }));
  };

  const renderPen = () =>
    activeText && (
      <>
        <Section title="رسم الاسم بالقلم" hint="يُرسم الاسم كأن قلماً يكتبه، ثم يمتلئ">
          <SwitchRow
            title="تفعيل الرسم بالقلم"
            desc="الاسم يظهر بحركة قلم، وتتحكم بكل شي فيه"
            icon={<PenTool size={15} strokeWidth={2.25} className="text-tg-hint" />}
            checked={isPenDraw}
            onChange={() => penSet({ reveal_style: isPenDraw ? null : "draw" })}
          />
        </Section>

        {isPenDraw && (
          <>
            <Section title="أسلوب القلم">
              <div className="grid grid-cols-3 gap-2">
                {([
                  { id: "draw", label: "قلم رفيع", Icon: PenTool },
                  { id: "marker", label: "ماركر سميك", Icon: Highlighter },
                  { id: "neon", label: "نيون متوهج", Icon: Lightbulb },
                ] as const).map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => penSet({ reveal_style: id })}
                    className="flex flex-col items-center gap-1.5 py-3 rounded-xl text-[11px] border"
                    style={{
                      background: activeText.reveal_style === id ? "rgba(59,130,246,.22)" : "rgba(255,255,255,.05)",
                      borderColor: activeText.reveal_style === id ? "rgba(59,130,246,.8)" : "rgba(255,255,255,.1)",
                    }}
                  >
                    <Icon size={18} strokeWidth={2} />
                    {label}
                  </button>
                ))}
              </div>
            </Section>

            <Section title="طريقة الرسم">
              {penChip(
                [
                  { id: "letters", label: "حرف حرف" },
                  { id: "wave", label: "موجة" },
                  { id: "together", label: "الكل مع بعض" },
                ],
                activeText.reveal_mode ?? "letters",
                (v) => penSet({ reveal_mode: v }),
              )}
              <div className="mt-3 text-xs text-tg-hint mb-1.5">اتجاه الرسم</div>
              {penChip(
                [
                  { id: "auto", label: "تلقائي" },
                  { id: "rtl", label: "من اليمين" },
                  { id: "ltr", label: "من اليسار" },
                  { id: "center", label: "من الوسط" },
                  { id: "top", label: "من فوق" },
                  { id: "bottom", label: "من تحت" },
                ],
                activeText.reveal_dir ?? "auto",
                (v) => penSet({ reveal_dir: v }),
              )}
              <div className="mt-3">
                <SliderControl
                  label="سرعة الرسم"
                  value={activeText.reveal_speed ?? 1}
                  min={0.5}
                  max={2.5}
                  step={0.1}
                  unit="×"
                  onChange={(v) => penSet({ reveal_speed: v })}
                />
              </div>
            </Section>

            <Section title="رأس القلم" hint="شكل اللي يمشي ويرسم الحروف">
              {penChip(
                [
                  { id: "none", label: "بدون" },
                  { id: "dot", label: "نقطة متوهجة" },
                  { id: "pencil", label: "قلم رصاص" },
                  { id: "spark", label: "بريق" },
                  { id: "star", label: "نجمة" },
                ],
                activeText.reveal_tip ?? "dot",
                (v) => penSet({ reveal_tip: v }),
              )}
              {(activeText.reveal_tip === "dot" || activeText.reveal_tip === "spark" || activeText.reveal_tip === "star" || !activeText.reveal_tip) &&
                (activeText.reveal_mode ?? "letters") === "letters" && (
                  <div className="mt-3">
                    <SwitchRow title="ذيل خلف القلم" desc="أثر يتلاشى وراه وهو يرسم" checked={!!activeText.reveal_trail} onChange={() => penSet({ reveal_trail: !activeText.reveal_trail })} />
                  </div>
                )}
              <div className="mt-3 text-xs text-tg-hint mb-1.5">شرر من رأس القلم</div>
              {penChip(
                [
                  { id: "none", label: "بدون" },
                  { id: "sparks", label: "شرر" },
                  { id: "stardust", label: "غبار نجوم" },
                ],
                activeText.reveal_fx ?? "none",
                (v) => penSet({ reveal_fx: v }),
              )}
            </Section>

            <Section title="بعد الرسم">
              <div className="text-xs text-tg-hint mb-1.5">ظهور التعبئة</div>
              {penChip(
                [
                  { id: "fade", label: "ظهور هادئ" },
                  { id: "pop", label: "ارتداد" },
                  { id: "rise", label: "صعود" },
                ],
                activeText.reveal_fill ?? "fade",
                (v) => penSet({ reveal_fill: v }),
              )}
              <div className="mt-3">
                <SwitchRow title="إبقاء خط القلم" desc="الخط يبقى حول النص بعد التعبئة" checked={!!activeText.reveal_keep} onChange={() => penSet({ reveal_keep: !activeText.reveal_keep })} />
              </div>
              <div className="mt-3 text-xs text-tg-hint mb-1.5">الخروج بنهاية المدة</div>
              {penChip(
                [
                  { id: "none", label: "يبقى" },
                  { id: "fade", label: "يختفي" },
                  { id: "erase", label: "يُمحى بالقلم" },
                ],
                activeText.reveal_out ?? "none",
                (v) => penSet({ reveal_out: v }),
              )}
            </Section>

            <button
              type="button"
              onClick={replayPen}
              className="h-11 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold text-white"
              style={{ background: "linear-gradient(145deg,#4C8DFF,#2F5BEA)", boxShadow: "0 10px 22px -10px rgba(59,130,246,.9)" }}
            >
              <Play size={16} fill="currentColor" />
              معاينة الرسم من البداية
            </button>
          </>
        )}
      </>
    );

  const body: Record<ToolKey, () => ReactNode> = {
    text: renderText,
    size: renderSize,
    color: renderColor,
    stroke: renderStroke,
    depth: renderDepth,
    gradient: renderGradient,
    curve: renderCurve,
    pen: renderPen,
    motion: renderMotion,
  };

  return (
    <div className="flex gap-2.5 px-3 pt-3 pb-6 items-start">
      <ToolDock pages={pages} activeKey={currentKey} onSelect={handleSelect} />

      <div className="flex-1 min-w-0 flex flex-col gap-3">
        <div ref={canvasWrapRef} style={{ scrollMarginTop: 68 }}>
          <StickerCanvas />
        </div>

        {currentKey && (
          <div ref={panelRef} className="flex flex-col gap-3 rounded-2xl" style={{ scrollMarginTop: 68 }}>
            <div className="flex items-center justify-between px-1">
              <h2 className="text-sm font-bold">{TOOL_TITLES[currentKey]}</h2>
              <button type="button" onClick={() => setTool(null)} className="text-[11px] text-tg-hint">
                إخفاء
              </button>
            </div>
            {body[currentKey]()}
          </div>
        )}
      </div>

      {toast && <ToolToast label={toast.label} Icon={toast.Icon} below={toast.below} onGo={scrollToPanel} />}

      {fontModalOpen && activeText && !isIcon && (
        <FontPickerModal
          value={activeText.font_id}
          onChange={(fontId) => updateActiveText({ font_id: fontId })}
          onClose={() => setFontModalOpen(false)}
        />
      )}
      {drawingPopoverOpen && <DrawingToolPopover onClose={() => setDrawingPopoverOpen(false)} />}
      {textColorModalOpen && activeText && (
        <ColorPickerModal
          title={isIcon ? "لون الشعار" : "لون النص"}
          value={activeText.text_color}
          presets={template.colors as ColorOut[]}
          onChange={(hex) => updateActiveText({ text_color: hex })}
          onClose={() => setTextColorModalOpen(false)}
        />
      )}
      {strokeColorModalOpen && activeText && (
        <ColorPickerModal
          title="لون الحدود (Stroke)"
          value={activeText.stroke_color}
          presets={template.colors as ColorOut[]}
          onChange={(hex) => updateActiveText({ stroke_color: hex })}
          onClear={() => updateActiveText({ stroke_color: null })}
          onClose={() => setStrokeColorModalOpen(false)}
        />
      )}
      {outlineColorModalOpen && activeText && (
        <ColorPickerModal
          title="لون البعد الثالث"
          value={activeText.outline_color}
          presets={template.colors as ColorOut[]}
          onChange={(hex) => updateActiveText({ outline_color: hex })}
          onClear={() => updateActiveText({ outline_color: null })}
          onClose={() => setOutlineColorModalOpen(false)}
        />
      )}
      {shineColorModalOpen && activeText && (
        <ColorPickerModal
          title="لون اللمعان"
          value={activeText.shine_color}
          presets={template.colors as ColorOut[]}
          onChange={(hex) => updateActiveText({ shine_color: hex })}
          onClear={() => updateActiveText({ shine_color: null })}
          onClose={() => setShineColorModalOpen(false)}
        />
      )}
    </div>
  );
}
